import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Table, Badge, Form, InputGroup } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import TenantManagementLayout from './TenantManagementLayout';

const TenantList = ({ auth }) => {
  const history = useHistory();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showDeactivated, setShowDeactivated] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [showDeactivated]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (showDeactivated) params.append('include_deactivated', 'true');

      const response = await fetch(`/api/admin/tenants?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch tenants');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccountLevelBadge = (level) => {
    const variants = {
      testing: 'secondary',
      standard: 'info',
      professional: 'primary',
      enterprise: 'success'
    };
    return <Badge bg={variants[level] || 'secondary'}>{level}</Badge>;
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(search.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <TenantManagementLayout title="All Tenants">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading tenants...</p>
        </div>
      </TenantManagementLayout>
    );
  }

  return (
    <TenantManagementLayout title="All Tenants" subtitle="View and manage all organization accounts">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search tenants..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center gap-3">
          {auth.platform_role === 'super_admin' && (
            <Form.Check
              type="switch"
              id="show-deactivated"
              label="Show deactivated"
              checked={showDeactivated}
              onChange={(e) => setShowDeactivated(e.target.checked)}
            />
          )}
          {['super_admin', 'admin'].includes(auth.platform_role) && (
            <Button
              variant="success"
              onClick={() => history.push('/admin/tenants/new')}
            >
              + Create Tenant
            </Button>
          )}
        </Col>
      </Row>

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={{ padding: '1rem' }}>Tenant</th>
                <th style={{ padding: '1rem' }}>Account Level</th>
                <th style={{ padding: '1rem' }}>Token Balance</th>
                <th style={{ padding: '1rem' }}>Users</th>
                <th style={{ padding: '1rem' }}>Status</th>
                <th style={{ padding: '1rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    {search ? 'No tenants match your search' : 'No tenants found'}
                  </td>
                </tr>
              ) : (
                filteredTenants.map(tenant => (
                  <tr key={tenant.id} style={{ opacity: tenant.is_active ? 1 : 0.6 }}>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <Link
                          to={`/admin/tenants/${tenant.id}`}
                          style={{ fontWeight: '600', color: '#1a1a2e', textDecoration: 'none' }}
                        >
                          {tenant.name}
                        </Link>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                          {tenant.slug}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {getAccountLevelBadge(tenant.account_level)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <span style={{ fontWeight: '600' }}>{formatNumber(tenant.monthly_tokens_used || 0)}</span>
                        <span style={{ color: '#64748b', fontSize: '0.85rem' }}> / {formatNumber(tenant.monthly_token_allocation)}</span>
                      </div>
                      {tenant.oneoff_token_balance > 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#22c55e' }}>
                          +{formatNumber(tenant.oneoff_token_balance)} bonus
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span>{tenant.user_count || 0}</span>
                      <span style={{ color: '#64748b', fontSize: '0.85rem' }}> / {tenant.seat_count || 10}</span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {tenant.is_active ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="danger">Deactivated</Badge>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => history.push(`/admin/tenants/${tenant.id}`)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="mt-4">
        <Col>
          <Card style={{ background: '#f8fafc', border: 'none' }}>
            <Card.Body>
              <Row className="text-center">
                <Col>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1a1a2e' }}>
                    {tenants.filter(t => t.is_active).length}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Active Tenants</div>
                </Col>
                <Col>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1a1a2e' }}>
                    {tenants.reduce((sum, t) => sum + (t.user_count || 0), 0)}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Total Users</div>
                </Col>
                <Col>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1a1a2e' }}>
                    {formatNumber(tenants.reduce((sum, t) => sum + (t.token_balance || 0), 0))}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Total Tokens</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantList);
